import React from 'react';
import { Box, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { gray80, red20, red50 } from '../../constants/colors';
import {
  reviewIssueCode,
  reviewIssueGroup,
  reviewIssueGroupLabel,
  reviewIssueGroupOrder,
  reviewIssueSource,
} from '../../constants/enum';
import type { ReviewIssue } from '../../types/reviewIssue';

/**
 * 要注意サマリーパネル（#3262）。
 *
 * OCR詳細画面の上部に、要注意と判定された項目名を理由ごとにまとめて並べる。
 * 項目名をクリックすると該当項目までスクロールする。
 *
 * 文言は「特に注意して確認をおすすめします」とする。
 *
 * 「確認してください」だと義務のニュアンスが出て、全件目視をお願いしている
 * 運用と衝突する（設計書 §1.3）。一方で「確認をおすすめします」だけだと、
 * 裏返しで「ここに出ていない項目は確認しなくてよい」と読めてしまう。
 * 「特に注意して」を入れることで、他も見る前提のうえで優先度を示す形になる。
 */

interface ReviewSummaryPanelProps {
  /** 要注意の一覧（詳細画面で判定したもの。#3328） */
  issues: ReviewIssue[];
  /** 項目名クリック時のスクロール処理 */
  onItemClick: (issue: ReviewIssue) => void;
}

type ReviewIssueGroup = (typeof reviewIssueGroup)[keyof typeof reviewIssueGroup];

/**
 * サマリーに出す項目名を組み立てる。
 *
 * 明細は行を跨いで並ぶため、ここでだけ行番号を添える。
 * セル下部・項目バッジは表示位置で行が分かるので label（項目名のみ）を使う。
 */
const buildDisplayLabel = (issue: ReviewIssue): string =>
  issue.row_index !== undefined ? `${issue.row_index + 1}行目: ${issue.label}` : issue.label;

/**
 * 判定 1 件をサマリーの表示グループに割り当てる。
 *
 * source だけだとマスタ照合と辞書変換が同じグループになるため、
 * 辞書由来（code）だけを切り出して 4 分類にする。
 */
const resolveGroup = (issue: ReviewIssue): ReviewIssueGroup => {
  if (issue.source === reviewIssueSource.validation) return reviewIssueGroup.validation;
  if (issue.source === reviewIssueSource.confidence) return reviewIssueGroup.confidence;
  // 辞書由来のコードは漏れなく辞書グループへ（#3339）。
  // noDictionaryMatch を入れ忘れると「辞書該当なし」がマスタ側に並ぶ。
  return issue.code === reviewIssueCode.lowDictionaryScore ||
    issue.code === reviewIssueCode.noDictionaryMatch
    ? reviewIssueGroup.dictionary
    : reviewIssueGroup.matching;
};

/** 1 項目（path）に複数の理由がある場合、どのグループに出すかを決める */
const resolveGroupPriority = (group: ReviewIssueGroup): number =>
  reviewIssueGroupOrder.indexOf(group);

interface GroupedIssue {
  /** 代表となる判定（表示ラベルとスクロール先の解決に使う） */
  issue: ReviewIssue;
}

/**
 * 判定一覧をグループ → 項目の形に整理する。
 *
 * 同じ項目が複数グループに現れると件数が多く見えるため、
 * **1 項目は優先度が最も高いグループにだけ**出す。
 *
 * 理由の詳細はホバーではなく、各項目の入力欄の下に「チップ＋理由」で常時表示する。
 * サマリーは全体像の把握が目的で、詳細は項目名をクリックして飛べばよい。
 */
const groupIssues = (issues: ReviewIssue[]): Map<ReviewIssueGroup, GroupedIssue[]> => {
  // path ごとに理由をまとめ、代表グループを決める
  const byPath = new Map<string, { issue: ReviewIssue; group: ReviewIssueGroup }>();

  for (const issue of issues) {
    const group = resolveGroup(issue);
    const existing = byPath.get(issue.path);

    if (!existing) {
      byPath.set(issue.path, { issue, group });
      continue;
    }

    // より優先度の高いグループが来たら代表を差し替える
    if (resolveGroupPriority(group) < resolveGroupPriority(existing.group)) {
      existing.group = group;
      existing.issue = issue;
    }
  }

  // グループ → 項目 の形に組み替える
  const grouped = new Map<ReviewIssueGroup, GroupedIssue[]>();
  byPath.forEach(({ issue, group }) => {
    const list = grouped.get(group);
    if (list) {
      list.push({ issue });
    } else {
      grouped.set(group, [{ issue }]);
    }
  });

  return grouped;
};

export const ReviewSummaryPanel: React.FC<ReviewSummaryPanelProps> = ({ issues, onItemClick }) => {
  if (issues.length === 0) return null;

  const grouped = groupIssues(issues);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        padding: '12px 16px',
        marginBottom: '16px',
        borderRadius: '8px',
        border: `1px solid ${red50}`,
        backgroundColor: red20,
      }}
    >
      <ErrorOutlineIcon sx={{ fontSize: '18px', color: red50, flexShrink: 0, mt: '1px' }} />
      <Box sx={{ minWidth: 0, width: '100%', textAlign: 'left' }}>
        <Typography sx={{ fontSize: '13px', color: red50, mb: '6px' }}>
          以下の項目は特に注意して確認をおすすめします
        </Typography>

        {reviewIssueGroupOrder.map(group => {
          const groupIssueList = grouped.get(group);
          if (!groupIssueList || groupIssueList.length === 0) return null;

          return (
            <Box
              key={group}
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                flexWrap: 'wrap',
                gap: '4px',
                mb: '2px',
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontSize: '12px',
                  color: red50,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {reviewIssueGroupLabel[group]}：
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', minWidth: 0 }}>
                {groupIssueList.map(({ issue }) => (
                  <Typography
                    key={issue.path}
                    component="span"
                    onClick={(): void => onItemClick(issue)}
                    sx={{
                      fontSize: '12px',
                      color: gray80,
                      cursor: 'pointer',
                      textDecoration: 'underline',
                      '&:hover': { color: red50 },
                    }}
                  >
                    {buildDisplayLabel(issue)}
                  </Typography>
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ReviewSummaryPanel;
